import type {
    CatalogImportLineRequest,
    CatalogImportRequest,
} from '@/api/handlers/resources/catalogImports';
import {
    sendableDecisions,
    type CatalogImportFile,
    type NamesDecisions,
    type ParsedLine,
} from '@/pages/catalogImport/store';

/**
 * One parsed line as the request sends it: the line as the store holds it
 * (edits included), marked `excluded` when its row is in `exclusions`. An
 * excluded row is still sent, so the server reports it back as excluded.
 * The cells the parse could not read (`unreadable`) are the app's own and
 * are never sent.
 */
export const toImportLine = (
    { unreadable: _unreadable, ...line }: ParsedLine,
    exclusions: ReadonlySet<number>,
): CatalogImportLineRequest =>
    exclusions.has(line.rowNumber) ? { ...line, excluded: true } : line;

export type CatalogImportRequestInput = {
    branchId: string;
    file: Pick<CatalogImportFile, 'name' | 'hash'> | null;
    lines: readonly ParsedLine[];
    decisions: NamesDecisions;
    exclusions: ReadonlySet<number>;
};

/**
 * The body of every preview and of the import, from the store's state: the
 * first preview (no decisions yet), each re-check, and the import send the
 * same shape, so what was previewed is what is imported.
 *
 * Decisions go in `names`, keyed by the text as the file has it, and never
 * into a line's ids: a line keeps its texts, so the next preview still lists
 * the name and the Names step can change its decision. A decision still
 * missing its value is left out (`sendableDecisions`): the server would
 * refuse the whole request for it.
 */
export const buildCatalogImportRequest = ({
    branchId,
    file,
    lines,
    decisions,
    exclusions,
}: CatalogImportRequestInput): CatalogImportRequest => ({
    branchId,
    fileName: file?.name ?? null,
    fileHash: file?.hash ?? null,
    lines: lines.map((line) => toImportLine(line, exclusions)),
    names: sendableDecisions(decisions),
});
